/**
 * Italian Codice Fiscale (CF) computation.
 * Requires: name, surname, gender (M/F), birth_date (yyyy-mm-dd), belfiore_code (4-char alphanumeric).
 * Belfiore code = comune di nascita code (e.g. Roma = H501, Milano = F205).
 * Returns 16-char uppercase string or null if insufficient input.
 */
const VOWELS = "AEIOU";
const MONTH_LETTERS = "ABCDEHLMPRST"; // Jan→Dec

const stripDiacritics = (s) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toUpperCase().replace(/[^A-Z]/g, "");

const surnameCode = (surname) => {
  const s = stripDiacritics(surname);
  const cons = s.replace(/[AEIOU]/g, "");
  const vow = s.replace(/[^AEIOU]/g, "");
  return (cons + vow + "XXX").slice(0, 3);
};

const nameCode = (name) => {
  const s = stripDiacritics(name);
  const cons = s.replace(/[AEIOU]/g, "");
  const vow = s.replace(/[^AEIOU]/g, "");
  // If 4+ consonants → take 1st, 3rd, 4th; else first 3 chars of consonants+vowels+XXX
  if (cons.length >= 4) return cons[0] + cons[2] + cons[3];
  return (cons + vow + "XXX").slice(0, 3);
};

const dateGenderCode = (birthDate, gender) => {
  const d = new Date(birthDate);
  if (Number.isNaN(d.getTime())) return null;
  const yy = String(d.getFullYear()).slice(-2);
  const month = MONTH_LETTERS[d.getMonth()];
  let day = d.getDate();
  if (gender === "F") day += 40;
  return yy + month + String(day).padStart(2, "0");
};

// Control character (CIN) algorithm
const ODD_VALUES = {
  "0": 1, "1": 0, "2": 5, "3": 7, "4": 9, "5": 13, "6": 15, "7": 17, "8": 19, "9": 21,
  A: 1, B: 0, C: 5, D: 7, E: 9, F: 13, G: 15, H: 17, I: 19, J: 21, K: 2, L: 4, M: 18,
  N: 20, O: 11, P: 3, Q: 6, R: 8, S: 12, T: 14, U: 16, V: 10, W: 22, X: 25, Y: 24, Z: 23,
};
const EVEN_VALUES = {
  "0": 0, "1": 1, "2": 2, "3": 3, "4": 4, "5": 5, "6": 6, "7": 7, "8": 8, "9": 9,
  A: 0, B: 1, C: 2, D: 3, E: 4, F: 5, G: 6, H: 7, I: 8, J: 9, K: 10, L: 11, M: 12,
  N: 13, O: 14, P: 15, Q: 16, R: 17, S: 18, T: 19, U: 20, V: 21, W: 22, X: 23, Y: 24, Z: 25,
};
const CONTROL_LETTERS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";

const controlChar = (code15) => {
  let sum = 0;
  for (let i = 0; i < 15; i++) {
    const ch = code15[i];
    sum += (i % 2 === 0 ? ODD_VALUES[ch] : EVEN_VALUES[ch]) || 0;
  }
  return CONTROL_LETTERS[sum % 26];
};

export function computeFiscalCode({ name, surname, gender, birth_date, belfiore_code }) {
  if (!name || !surname || !gender || !birth_date || !belfiore_code) return null;
  if (!/^[A-Z][0-9]{3}$/i.test(belfiore_code)) return null;
  const sc = surnameCode(surname);
  const nc = nameCode(name);
  const dc = dateGenderCode(birth_date, gender);
  if (!sc || !nc || !dc || sc.length !== 3 || nc.length !== 3) return null;
  const code15 = (sc + nc + dc + belfiore_code).toUpperCase();
  if (code15.length !== 15) return null;
  return code15 + controlChar(code15);
}
